import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';

export interface UseFileUploadProps {
  /** Called with the selected File (and object URL for image previews). */
  onUpload?: (file: File, previewUrl: string | null) => void;
  /** When true, keep prior selections and append (multiple mode). */
  multiple?: boolean;
}

/**
 * Shared file-picker state for evidence uploads (PDF, CSV, images, etc.).
 * Returns a real File — not only a blob URL — so parents can POST to APIs.
 */
export function useFileUpload({ onUpload, multiple = false }: UseFileUploadProps = {}) {
  const previewRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const revokePreview = useCallback(() => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
  }, []);

  const applyFile = useCallback(
    (next: File) => {
      revokePreview();
      setFileName(next.name);
      setFile(next);
      const isImage = next.type.startsWith('image/');
      const url = isImage ? URL.createObjectURL(next) : null;
      if (url) {
        previewRef.current = url;
      }
      setPreviewUrl(url);
      onUpload?.(next, url);
    },
    [onUpload, revokePreview],
  );

  const handleThumbnailClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const list = event.target.files;
      if (!list?.length) return;

      if (multiple) {
        const next = Array.from(list);
        setFiles(next);
        setFile(next[0] ?? null);
        setFileName(next[0]?.name ?? null);
        const first = next[0];
        if (first?.type.startsWith('image/')) {
          revokePreview();
          const url = URL.createObjectURL(first);
          previewRef.current = url;
          setPreviewUrl(url);
          onUpload?.(first, url);
        } else {
          revokePreview();
          setPreviewUrl(null);
          if (first) onUpload?.(first, null);
        }
        return;
      }

      const selected = list[0];
      if (selected) applyFile(selected);
    },
    [applyFile, multiple, onUpload, revokePreview],
  );

  const handleRemove = useCallback(() => {
    revokePreview();
    setPreviewUrl(null);
    setFileName(null);
    setFile(null);
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [revokePreview]);

  /** Programmatically stage a File (e.g. canvas drop → Bank import). */
  const setSelectedFile = useCallback(
    (next: File | null) => {
      if (!next) {
        handleRemove();
        return;
      }
      applyFile(next);
    },
    [applyFile, handleRemove],
  );

  useEffect(() => {
    return () => {
      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current);
      }
    };
  }, []);

  return {
    previewUrl,
    fileName,
    file,
    files,
    fileInputRef,
    handleThumbnailClick,
    handleFileChange,
    handleRemove,
    setSelectedFile,
  };
}
